/* eslint-disable max-classes-per-file */
import dayjs from 'lib/dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import AddFields from 'helpers/nested-fields/addFields';
import isEqual from 'lodash.isequal';
import { nextFrame } from 'helpers/timing';
import { onAutoSaveSuspend, openFormStore } from 'lib/form_store';
import { metaValue } from 'helpers/dom-helper';
import axios from 'axios';
import { refreshCsrfToken, sameUserAsPage } from 'lib/direct_upload_with_retry';
import { regionFields } from 'helpers/form-region';
import ApplicationController from './application_controller';

dayjs.extend(relativeTime);

// The id prefix a server draft takes among the browser copies (theirs are numbers).
const SERVER_COPY_PREFIX = 'server:';

// What became of a copy sent to the server (ServerDrafts#push).
const PUSH_STORED = 'stored';
// The server could not take it now and may later: offline, timed out, signed out, busy, or a write race.
const PUSH_UNREACHABLE = 'unreachable';
// The server answered that it will not take it: too large, invalid, or server drafts are off.
const PUSH_REFUSED = 'refused';
// 4xx answers that are worth trying again: signed out (401, the clinician may sign back in), request timeout (408),
// two writes of a new draft raced (409, FormDraftsController#update), rate limited (429).
const RETRYABLE_STATUSES = [401, 408, 409, 429];

export default class extends ApplicationController {
  // How long a held Save may wait for the guard to finish before it is sent anyway. Longer than the whole chain
  // (storing 2s, the session check 5s, marking 2s), so a slow but working guard is never cut short; it catches what
  // nothing else times out. Specs shorten it.
  static saveWatchdog = 10_000;

  // How long a held Save waits for one browser storage step (storing or marking the copy). A step that takes
  // longer is treated as failed and the Save moves on, so a hung IndexedDB can never keep the session check from
  // running: sending to an expired session with no copy kept would lose the work. Specs shorten it.
  static storageLimit = 2_000;

  initialize() {
    this.onSubmit = this.onSubmit.bind(this);
    this.onSuccessfulSubmission = this.onSuccessfulSubmission.bind(this);
    this.onUnSuccessfulSubmission = this.onUnSuccessfulSubmission.bind(this);
    this.loadHistory = this.loadHistory.bind(this);
    this.onSessionTimeout = this.onSessionTimeout.bind(this);
    this.refreshHistory = this.refreshHistory.bind(this);
    this.save = this.save.bind(this);
    this.suspend = this.suspend.bind(this);

    this.db = openFormStore();
  }

  async connect() {
    if (!this.shouldAutoSave) return;
    if (!this.element.id) throw new Error('<form> element should have a unique `id`');

    await this.db.open();
    this.formHistory = new FormHistory(this.db, { form: this.element, historyLimit: this.autoSaveHistoryLength });
    // Native form submits bypass `ajax:success`, so post-submit entries are only marked `submitted: 'true'`.
    // Whether that submit reached the server is settled here, on the next render of the form.
    await this.formHistory.reconcileSubmitted();

    this.element.addEventListener('submit', this.onSubmit);
    this.element.addEventListener('ajax:success', this.onSuccessfulSubmission);
    this.element.addEventListener('ajax:error', this.onUnSuccessfulSubmission);
    document.addEventListener('auto-save--history:load', this.loadHistory);
    document.addEventListener('auto-save--history:refresh', this.refreshHistory);
    document.addEventListener('session-timeout', this.onSessionTimeout);
    this.stopListeningForSuspend = onAutoSaveSuspend(this.suspend);

    this.startAutoSaving();

    // Let the page's own widgets settle before comparing the rendered form with the newest draft.
    await nextFrame();
    // The server's draft may come from another device, and this browser may hold work the server never got.
    await this.formHistory.syncWithServer();
    await this.formHistory.offerUnsavedWork();
  }

  startAutoSaving() {
    if (this.interval) clearInterval(this.interval);
    this.interval = setInterval(this.autoSave.bind(this), this.autoSaveInterval * 1000);
  }

  disconnect() {
    this.formHistory?.destroy();

    this.element.removeEventListener('submit', this.onSubmit);
    this.element.removeEventListener('ajax:success', this.onSuccessfulSubmission);
    this.element.removeEventListener('ajax:error', this.onUnSuccessfulSubmission);
    document.removeEventListener('auto-save--history:load', this.loadHistory);
    document.removeEventListener('auto-save--history:refresh', this.refreshHistory);
    document.removeEventListener('session-timeout', this.onSessionTimeout);
    this.stopListeningForSuspend?.();

    if (this.interval) clearInterval(this.interval);
  }

  /**
   * Two kinds of submit arrive here: a Save button click, and `form.requestSubmit()` with no submitter, which is
   * how the custom form Save handler submits after its own validation. Both are stopped, checked, and then sent
   * natively. Turbo Drive is off app-wide (`<html data-turbo="false">`), so there is no Turbo submission to hand
   * back to: `submit()` is what the base behaviour already did for button clicks.
   */
  async onSubmit(event) {
    // A capture-phase guard (e.g. required-fields) may have refused this submit. Honor the
    // refusal — resubmitting natively below would bypass it entirely.
    if (event.defaultPrevented) return;
    if (this.isRemote) return;
    // Already checking or already leaving. A `disable-on-submit` button is handed back the moment this handler
    // holds its event, so a second click can arrive while the session check is still in flight.
    if (this.submitting) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    // Awaited, and with the timer stopped and in-flight autosaves refused, so no draft can be written after the
    // rest are marked as submitted.
    if (this.interval) clearInterval(this.interval);
    this.submitting = true;
    this.heldWithoutCopy = false;

    // Holding the browser's submit is only safe if the Save always ends: sent, or held with a message saying why.
    // Every step below fails open, but a storage call can hang (IndexedDB blocked by another tab) and an error
    // nobody expected would leave the Save button doing nothing. So the Save settles exactly once, and if nothing
    // has settled it by the watchdog, or anything throws, it is sent as it was before this guard existed.
    const outcome = this.settleOnce();
    const watchdog = setTimeout(() => outcome.send(), this.constructor.saveWatchdog);
    outcome.onSettle(() => clearTimeout(watchdog));

    try {
      const copyStored = await this.storeCopy();
      outcome.copyStored = copyStored;

      if (!this.isOnline) {
        outcome.hold(() => showOfflineHold(copyStored));
        return;
      }

      // A submit the server would refuse (expired session, stale CSRF token) replaces the page with the sign in
      // screen and the typed work with it. Find out first, while the form is still on screen.
      const session = await checkSession();
      if (outcome.settled) return;
      // The connection dropped while the check was in flight, so its answer is a network error, not a session.
      // Hold as offline now, before anything is marked: a marked copy leaves the history.
      if (!this.isOnline) {
        outcome.hold(() => showOfflineHold(copyStored));
        return;
      }
      if (session.expired) {
        outcome.hold(() => {
          const explained = showSessionExpiredModal({ ...session, copyStored });
          // Read by onSessionTimeout, to keep the session poller from replacing the only copy of the work.
          this.heldWithoutCopy = explained && !copyStored;
          return explained;
        });
        return;
      }

      try {
        await withinTime(this.formHistory?.markOwnCopiesSubmitted(), this.constructor.storageLimit);
      } catch {
        // Storage went away, or is too slow, between the write and here. The work is on its way to the server
        // either way.
      }
      outcome.send();
    } catch {
      outcome.send();
    }
  }

  // The two ways a held Save ends, guarded so only the first one counts. `send` is the browser's own submit, unless
  // the browser is offline: that would replace the page with its own error, so the Save is held with the offline
  // toast instead. `hold` shows why the Save was stopped and hands the form back; if that cannot be shown (it throws,
  // or reports `false`), the Save is sent, so a missing modal can never trap the work on a page that will not save.
  // Offline it holds regardless: sending would lose the page.
  settleOnce() {
    let settled = false;
    let onSettle = () => {};
    const outcome = {
      // Whether the copy was stored, once that step has run. Until then no copy is claimed.
      copyStored: false,
      get settled() {
        return settled;
      },
      onSettle: (callback) => {
        onSettle = callback;
      },
      send: () => {
        if (settled) return;
        if (!this.isOnline) {
          outcome.hold(() => showOfflineHold(outcome.copyStored));
          return;
        }
        settled = true;
        onSettle();
        // The browser now leaves the page, which can take seconds on a long document. Nothing is resumed: the Save
        // buttons are locked (a second click would file a second document, and no create has an idempotency key;
        // a `disable-on-submit` button was handed back when this handler held its event), and `submitting` keeps
        // the timer from writing a copy nobody will ever reconcile.
        this.submitControls.forEach((button) => {
          button.disabled = true;
        });
        // Not waited for: the request outlives the page, and the Save goes now.
        this.formHistory?.markSubmittedOnServer();
        this.element.submit();
      },
      hold: (explain) => {
        if (settled) return;
        let explained;
        try {
          explained = explain() !== false;
        } catch {
          explained = false;
        }
        if (!explained && this.isOnline) {
          outcome.send();
          return;
        }
        settled = true;
        onSettle();
        this.resumeEditing();
      },
    };
    return outcome;
  }

  // The copy is a safety net, so a storage failure or hang (quota, a lost or blocked IndexedDB) must never cost
  // the save: the submit goes on to the session check either way. Returns whether a copy now exists, so nothing on
  // screen claims one when it does not.
  async storeCopy() {
    try {
      // Not sent to the server: the Save itself carries this work. Sent now, it could land after the mark the Save
      // sends as it leaves, as a draft nobody marked. If the Save fails, this copy goes up on the next visit.
      await withinTime(this.formHistory?.save(false, { push: false }), this.constructor.storageLimit);
      return true;
    } catch {
      return false;
    }
  }

  // Includes buttons outside the form that join it through their `form` attribute (the legacy visit note Save).
  get submitControls() {
    return Array.from(this.element.elements).filter((element) => element.type === 'submit');
  }

  // The submit was stopped, so hand the form back. The timer was cleared, and the Save button was disabled either
  // by Rails UJS or by the custom form Save handler, which also swapped its label for a spinner and stashed the
  // original in `data-original-html`.
  resumeEditing() {
    this.submitting = false;
    this.startAutoSaving();
    // Need to wait for sometime to avoid race conditions
    setTimeout(() => {
      this.submitControls.forEach((button) => {
        button.removeAttribute('disabled');
        if (button.dataset.originalHtml) button.innerHTML = button.dataset.originalHtml;
      });
    }, 100);
  }

  /**
   * If the form submission is successful, let's delete all the form related data. This is not true for `onSubmit`
   * handler because it does not use Turbo's event, and hence it is not possible to know if we really did write on
   * the database. In that case, we mark `submitted` as `true` locally.
   * We recommend using Turbo with `form_for` or `form_with` unless there is a solid reason not to.
   */
  async onSuccessfulSubmission() {
    await this.formHistory?.histories.delete();
    await this.formHistory?.server?.remove();
  }

  async onUnSuccessfulSubmission() {
    this.formHistory?.save(false); // Store a fresh copy of the form fields
    if (!this.isOnline) $('#offline-form-saved').toast('show');
  }

  async loadHistory(event) {
    const { id } = event.detail;
    if (!id) return;

    await this.formHistory?.load(id);
    this.dispatch('loaded');
  }

  async refreshHistory() {
    await this.formHistory?.populateHistories();
    this.dispatch('history-in-sync', { bubbles: true });
  }

  // The poller waits for this before it sends the browser to the sign in page, so hand it the write. While a Save
  // is held and no copy could be stored, the page is the only place the work exists and the modal has told the
  // clinician to stay on it, so the redirect is refused. The write is still tried: once a copy exists, the next
  // time the poller asks it may go.
  onSessionTimeout(event) {
    if (this.heldWithoutCopy) event.preventDefault();
    const stored = this.save();
    event.detail?.waitUntil?.(stored);
  }

  // The person signed out, here or in another tab, and their copies of the forms whose id starts with `formIdPrefix`
  // were deleted (lib/form_store.js `suspendAutoSave`). The timer and the session poller's final copy stop for good on
  // such a form, or one would be written straight back to what may be a shared device. Their other forms (a visit note
  // as staff) keep autosaving, and so does anything the person does on the page: a Save still stores its copy.
  suspend({ userId, formIdPrefix } = {}) {
    if (!formIdPrefix || !this.element.id.startsWith(formIdPrefix)) return;
    if ((userId || null) !== currentUserId()) return;
    this.suspended = true;
    if (this.interval) clearInterval(this.interval);
  }

  async autoSave() {
    const saved = await this.save();
    if (saved) {
      await this.checkIfHistoryOutOfSync();
    }
  }

  async save(showToast = true) {
    if (!this.shouldAutoSave || this.submitting || this.suspended) return false;
    const isDirty = await this.isFormDirty();
    // Either way the browser now holds what is on the page.
    if (!isDirty) {
      this.heldWithoutCopy = false;
      // Nothing new to keep, but the server may still be missing the last copy (it could not be reached).
      await this.formHistory?.catchUpServer();
      return false;
    }
    await this.formHistory?.save(showToast);
    this.heldWithoutCopy = false;
    return true;
  }

  // Private

  async isFormDirty() {
    // Check if form fields have changed
    const lastHistory = await this.formHistory?.histories.last();
    return !isEqual(lastHistory?.children, traverseDOM(this.element));
  }

  get shouldAutoSave() {
    return !!metaValue('auto-save');
  }

  get autoSaveInterval() {
    return this.parseIntFromMeta('auto-save-interval', 30);
  }

  get autoSaveHistoryLength() {
    return this.parseIntFromMeta('auto-save-history-length', 10);
  }

  parseIntFromMeta(name, defaultValue) {
    const value = Number.parseInt(metaValue(name), 10);
    return Number.isNaN(value) ? defaultValue : value;
  }

  get isRemote() {
    return this.element.hasAttribute('data-remote');
  }

  // If we save the last history when the history modal is still open, we need to let the users know that the history
  // is out-of-sync
  async checkIfHistoryOutOfSync() {
    const afterSaveLastHistory = await this.formHistory?.histories.last();
    const recentHistoryInView = document.querySelector('[data-history-item]');
    if (!recentHistoryInView) return;

    const datetime = recentHistoryInView.getAttribute('datetime');
    if (afterSaveLastHistory.time > Number(datetime)) {
      this.dispatch('history-out-of-sync', { bubbles: true });
    }
  }

  get isOnline() {
    if ('onLine' in navigator) {
      return !!navigator.onLine;
    }

    return true;
  }
}

class FormHistory {
  constructor(db, { form, historyLimit }) {
    this.db = db;
    this.form = form;
    this.storageKey = computeStorageKey(form);
    this.historyLimit = historyLimit;
    this.recordVersion = form.dataset.autoSaveRecordVersion || null;
    // Tags every copy this page load writes, so a Save here marks only its own copies. A second tab on the same
    // form shares the storage bucket, and its unsaved work must not be marked (and later cleaned up) by this Save.
    this.pageId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    // Page loads whose work this page carries, each with the newest copy of it this page holds: every copy of its
    // own, and for a page whose copy was restored into it, copies up to the last one restored. A Save marks those,
    // so a copy restored after a timeout and then saved here is not offered again. The restored page may still be
    // open in another tab and go on writing; its later copies are that tab's work and are left alone. So is a newer
    // copy the clinician moved away from by restoring an older one.
    this.lineage = new Map([[this.pageId, Infinity]]);
    this.historyModal = document.getElementById('history-modal');
    this.unsavedWorkBanner = document.getElementById('auto-save-unsaved-work');
    // The server keeps this user's drafts of the form, one per visit (page load), the newest few; the copies in this
    // browser are the backup. Null when server drafts are off for the organization.
    this.server = serverDraftsEnabled() ? new ServerDrafts(this.storageKey) : null;
    // The server's drafts, in the shape of browser copies, that hold work no copy here does.
    this.serverDrafts = [];
    // Whether the server is missing work this browser has: the last push failed, or a check could not reach it.
    this.serverBehind = false;
    // The browser copy the server last refused (too large, invalid, or server drafts turned off). It would be refused
    // again, so it is not re-sent: each re-send is an upload of the whole form, and a request that keeps an
    // unattended session busy. New work is still sent as it is typed.
    this.refusedCopyId = null;
    // The browser time of the copy the server holds, for the "Draft saved" label.
    this.serverSavedAt = null;
    this.lastPush = Promise.resolve(false);

    // Update "Last Saved" time every 1 second.
    this.interval = setInterval(this.updateLastSavedAt.bind(this), 60 * 1000);

    const autoSaveMenu = document.getElementById('auto-save-menu');
    if (!autoSaveMenu) return;

    const slot = document.querySelector('[data-auto-save-slot]');
    if (slot) {
      slot.appendChild(autoSaveMenu);
      autoSaveMenu.classList.add('is-slotted');
      autoSaveMenu.style.display = 'inline-flex';

      // When the slot scrolls past the fixed page header (~70px), pin the
      // menu to the top-right of the viewport so the controls stay reachable.
      // When the slot scrolls back into view, the pin releases.
      this.pinObserver = new IntersectionObserver(
        ([entry]) => {
          autoSaveMenu.classList.toggle('is-floating', !entry.isIntersecting);
        },
        { rootMargin: '-70px 0px 0px 0px', threshold: 0 }
      );
      this.pinObserver.observe(slot);
    } else {
      autoSaveMenu.classList.remove('is-slotted');
      autoSaveMenu.style.display = 'block';
    }

    this.historyBtn = autoSaveMenu.querySelector('#auto-save-history-button');
    this.manualSaveBtn = autoSaveMenu.querySelector('#auto-save-manual-button');
    this.loadLastHistoryBtn = autoSaveMenu.querySelector('#auto-save-load-button');
    if (!this.historyBtn || !this.manualSaveBtn || !this.loadLastHistoryBtn) return;

    this.onHistoryBtnClick = this.onHistoryBtnClick.bind(this);
    this.onManualSaveBtnClick = this.onManualSaveBtnClick.bind(this);
    this.onLoadLastHistoryBtnClick = this.onLoadLastHistoryBtnClick.bind(this);
    this.appendHistoryItem = this.appendHistoryItem.bind(this);
    this.populateElement = this.populateElement.bind(this);

    this.historyBtn.addEventListener('click', this.onHistoryBtnClick);
    this.manualSaveBtn.addEventListener('click', this.onManualSaveBtnClick);
    this.loadLastHistoryBtn.addEventListener('click', this.onLoadLastHistoryBtnClick);

    // This should be invoked after initializing everything.
    this.updateLastSavedAt();
  }

  destroy() {
    this.historyBtn?.removeEventListener('click', this.onHistoryBtnClick);
    this.manualSaveBtn?.removeEventListener('click', this.onManualSaveBtnClick);
    this.loadLastHistoryBtn?.removeEventListener('click', this.onLoadLastHistoryBtnClick);
    this.pinObserver?.disconnect();
    if (this.interval) clearInterval(this.interval);
    this.hideUnsavedWorkBanner();
  }

  async load(id) {
    const history = id.toString().startsWith(SERVER_COPY_PREFIX)
      ? this.serverDrafts.find((draft) => draft.id === id.toString())
      : (await this.histories.toArray()).find((h) => h.id.toString() === id.toString());
    if (!history) return;
    await this.loadCopy(history);
  }

  async loadCopy(history) {
    this.carryForward(history, { restored: true });

    await Promise.all(history.children.map((child) => this.populateElement(child)));
    $(this.historyModal).modal('hide');
    this.hideUnsavedWorkBanner();
    // Notify listeners (rich-text editors etc.) that the form was restored.
    // The history-modal path also dispatches this from the outer controller,
    // but the floating "Load last save" button skips that path.
    this.form.dispatchEvent(new CustomEvent('auto-save:loaded', { bubbles: true }));
  }

  async populateElement(object) {
    const { id, children, htmlDump } = object;

    if (htmlDump) {
      const container = document.getElementById(id);
      if (!container) return;

      container.innerHTML = htmlDump;
      await nextFrame();
      children.forEach((child) => findAndUpdateElement(child));
      showRestoredPicks(container);
    } else {
      findAndUpdateElement(object);
    }

    // Initialize add field buttons so that new nested fields can be appended
    // TODO: Implement `MutationObserver` on `AddFields` so that it can automatically detect for DOM changes
    await nextFrame();
    // eslint-disable-next-line no-new
    new AddFields();
  }

  async onHistoryBtnClick(event) {
    event.preventDefault();
    $(event.currentTarget).tooltip('hide');
    $(event.currentTarget).trigger('blur');

    const unsubmittedDataCount = (await this.histories.count()) + this.serverDrafts.length;
    if (!unsubmittedDataCount) {
      const historyBody = this.historyModal.querySelector('[data-histories]');
      historyBody.replaceChildren();

      const newRow = document.createElement('tr');
      const newCell = document.createElement('td');

      const emptyText = document.createElement('em');
      emptyText.textContent =
        this.historyModal.dataset.emptyText || "There isn't any history that you can restore for this form, yet.";
      newCell.appendChild(emptyText);
      newCell.setAttribute('colspan', '2');

      newRow.appendChild(newCell);
      historyBody.appendChild(newRow);
    }

    if (!this.historyModal) return;
    if (unsubmittedDataCount) await this.populateHistories();
    this.populateFormTitle();
    // Only show modal after appending all the histories
    $(this.historyModal).modal('show');
  }

  // Display which form these saved drafts belong to. Custom-form pages opt
  // in by setting `data-auto-save-title` from `template.title`, in which
  // case we render "Template: <name>". Forms outside the custom-form system
  // fall back to the page `<title>`, stripping the " | Ambiki" suffix
  // added by `full_title`.
  populateFormTitle() {
    const target = this.historyModal.querySelector('[data-form-title]');
    if (!target) return;

    const templateTitle = (this.form.dataset.autoSaveTitle || '').trim();
    const fallback = document.title.replace(/\s*\|\s*Ambiki\s*$/, '').trim();
    const templateLabel = this.historyModal.dataset.templateTitle || 'Template: %{title}';
    const label = templateTitle ? templateLabel.replace('%{title}', templateTitle) : fallback;

    if (label) {
      target.textContent = label;
      target.hidden = false;
    } else {
      target.textContent = '';
      target.hidden = true;
    }
  }

  async onManualSaveBtnClick(event) {
    event.preventDefault();
    $(event.currentTarget).tooltip('hide');
    $(event.currentTarget).trigger('blur');
    await this.save();
  }

  async onLoadLastHistoryBtnClick(event) {
    event.preventDefault();
    $(event.currentTarget).tooltip('hide');
    $(event.currentTarget).trigger('blur');
    const lastHistory = await this.newestCopy();
    if (!lastHistory) {
      // Nothing saved yet — let the user know with a self-dismissing toast rather than an
      // uninitialized popover, which used to wedge open between the buttons until a refresh.
      $('#autosave-no-history-toast').toast('show');
      return;
    }
    await this.loadCopy(lastHistory);
  }

  async save(showToast = true, { push = true } = {}) {
    const copy = this.constructDataHash({ children: traverseDOM(this.form) });
    copy.id = await this.db.forms.add(copy);
    const totalCount = await this.histories.count();
    const diff = totalCount - this.historyLimit;
    if (diff > 0) {
      await this.histories.limit(diff).delete();
    }

    // Not awaited: the browser copy is what a held Save waits for (`storeCopy` gives it 2 seconds), and a slow
    // server must not make it look as if no copy was kept.
    this.lastPush = push ? this.pushToServer(copy) : Promise.resolve(false);
    if (showToast) {
      this.lastPush.then((pushed) => showSavedToast(pushed));
    }

    // Update "Last Saved" time after saving the data. With server drafts the push does it once it knows where the
    // copy went; doing it here too would race the push and could put the browser wording back.
    if (!this.server || !push) await this.updateLastSavedAt();
  }

  // Sends a browser copy to the server as the draft of the visit that wrote it. Never throws: when the server cannot
  // take it, the browser copy is the backup and the next autosave (or the next page load) tries again.
  async pushToServer(copy) {
    if (!this.server) return false;
    const result = await this.server.push(copy);
    const pushed = result === PUSH_STORED;
    this.serverBehind = !pushed;
    if (result === PUSH_REFUSED) this.refusedCopyId = copy.id;
    if (pushed) {
      this.serverSavedAt = Math.max(this.serverSavedAt || 0, copy.time);
      // The server's draft of that visit now holds this copy, so it is no longer a separate save point.
      this.serverDrafts = this.serverDrafts.filter((draft) => draft.page_id !== copy.page_id);
    }
    await this.updateLastSavedAt();
    return pushed;
  }

  // A Save is leaving the page. Marks the server drafts of the visits whose work it carries (this page, and any page
  // whose copy was restored into it), so the next visit can drop them once the document has moved on. Fire and
  // forget: a mark that never arrives only leaves a draft for the 30-day sweep, and the banner already skips a draft
  // written against an older version of the document.
  markSubmittedOnServer() {
    this.server?.markSubmitted([...this.lineage.keys()]);
  }

  // The server missed a copy (it could not be reached). Send the newest one again, unless the server refused it.
  async catchUpServer() {
    if (!this.server || !this.serverBehind) return;
    const newest = await this.newestPushableCopy();
    if (newest && newest.id !== this.refusedCopyId) await this.pushToServer(newest);
  }

  // Reads the server's drafts when the form opens. Drafts holding work no copy here does (another device, or a
  // browser whose copies were cleared) are kept to offer. When this browser holds work the server never got (Ambiki
  // could not be reached when it was written), that copy is sent up now as its visit's draft.
  async syncWithServer() {
    if (!this.server) return;
    const drafts = await this.server.fetch(this.recordVersion);
    if (drafts === undefined) {
      this.serverBehind = true;
      return;
    }

    const copies = await this.histories.toArray();
    // This browser holds that visit's work, as new or newer, so the draft is not a separate save point.
    const heldHere = (draft) => copies.some((copy) => copy.page_id === draft.page_id && copy.time >= draft.saved_at);
    this.serverDrafts = drafts.filter((draft) => !heldHere(draft)).map((draft) => this.copyFromServer(draft));
    if (drafts.length) this.serverSavedAt = Math.max(...drafts.map((draft) => draft.saved_at));

    const local = await this.newestPushableCopy();
    const serverHas = (copy) => drafts.some((draft) => draft.page_id === copy.page_id && draft.saved_at >= copy.time);
    if (local && !serverHas(local)) await this.pushToServer(local);
    await this.updateLastSavedAt();
  }

  // The newest copy this browser holds that the server should have: this user's, written against the version on
  // screen, and not part of a Save on a new document (that may already be a document, see `reconcileSubmitted`).
  async newestPushableCopy() {
    const copies = await this.histories.toArray();
    return copies
      .reverse()
      .find(
        (copy) =>
          !!copy.page_id &&
          (copy.user_id || null) === currentUserId() &&
          (copy.record_version || null) === this.recordVersion &&
          copy.was_submitted !== 'true'
      );
  }

  // A server draft in the shape of a browser copy, so the banner, the history and a restore treat both alike.
  // A draft that was part of a Save carries a `submit_id`, as a marked browser copy does.
  //
  // On a new document every server draft is `was_submitted`, marked or not, so none is offered in the banner; each
  // stays in the history with its "Saved to Ambiki" tag. There is no version to tell a draft whose create landed from
  // one whose create failed, and the mark that says a Save carried it is fire and forget: it can be lost (the
  // keepalive fails, or a stale CSRF token is redirected) or overtaken (it reaches the server before the visit's
  // first draft does). Offering an unmarked draft that is already a document invites a second one on every other
  // device for 30 days. Browser copies of unknown fate are handled the same way (`reconcileSubmitted`), and this
  // device's own copies still offer this device's unsaved work.
  copyFromServer(draft) {
    const id = `${SERVER_COPY_PREFIX}${draft.page_id}`;
    return {
      id,
      form_id: this.storageKey,
      time: draft.saved_at,
      children: draft.snapshot,
      record_version: draft.record_version,
      page_id: draft.page_id,
      user_id: currentUserId(),
      submit_id: draft.submitted ? id : undefined,
      was_submitted: this.recordVersion ? undefined : 'true',
      fromServer: true,
    };
  }

  // Every save point for this form, newest first: the copies in this browser and the server's drafts.
  async allCopiesNewestFirst() {
    const copies = await this.histories.toArray();
    copies.push(...this.serverDrafts);
    return copies.sort((a, b) => b.time - a.time);
  }

  async newestCopy() {
    return (await this.allCopiesNewestFirst())[0];
  }

  async populateHistories() {
    if (!this.historyModal) return;

    const historyBody = this.historyModal.querySelector('[data-histories]');
    historyBody.replaceChildren();

    const histories = await this.allCopiesNewestFirst();
    histories.forEach(this.appendHistoryItem);
  }

  appendHistoryItem(history) {
    const historyContainer = this.historyModal.querySelector('[data-histories-container]');
    const historyBody = this.historyModal.querySelector('[data-histories]');
    const template = historyContainer.querySelector('[data-history-item-template]');

    const clonedHistoryItem = template.content.cloneNode(true);
    const trContainer = clonedHistoryItem.querySelector('[data-history-item]');
    const datetimeContainer = clonedHistoryItem.querySelector('[data-datetime]');
    const loadHistoryBtn = clonedHistoryItem.querySelector('[data-load-history-btn]');

    trContainer.setAttribute('datetime', history.time.toString());
    datetimeContainer.textContent = new Date(history.time).toLocaleString(dateLocale());
    if (history.fromServer) {
      const source = document.createElement('span');
      source.className = 'badge badge-info ml-2';
      source.textContent = this.historyModal.dataset.serverLabel || 'Saved to Ambiki';
      datetimeContainer.append(source);
    }
    loadHistoryBtn.setAttribute('data-id', history.id);

    historyBody.append(clonedHistoryItem);
  }

  constructDataHash(hash = {}) {
    // Using a string `false` to avoid: https://stackoverflow.com/questions/48149851/failed-to-execute-only-on-idbkeyrange-the-parameter-is-not-a-valid-key
    // "This error is generally thrown when you are passing - boolean, null or undefined value."
    return {
      form_id: this.storageKey,
      time: Date.now(),
      submitted: 'false',
      record_version: this.recordVersion,
      page_id: this.pageId,
      user_id: currentUserId(),
      ...hash,
    };
  }

  // Drafts are marked `submitted` before a submit leaves the page, when nobody knows yet whether the server will
  // accept it. This render settles it.
  //
  // Every copy one Save marked is one line of work, and only its newest copy is kept: the older ones are earlier
  // states of the same typing, and a refused Save that re-renders the form (the evaluation rescues do) leaves the
  // version stamp where it was, so an older copy would otherwise be offered over the newer text on the page. The
  // newest copy is kept even when the page already holds it, because until the next autosave nothing else does,
  // and a tab closed after a refused Save would lose it. A copy that matches the page is never offered.
  //
  // A moved version is deliberately NOT treated as proof this submit landed. Marking flags the whole bucket for
  // this form, so a second tab's unsaved draft gets flagged by the first tab's Save, and any other writer
  // (a co-signature, a supervisor's edit) moves the version too. Deleting on that would throw away the one copy
  // of someone's work. The version decides what to OFFER (`offerUnsavedWork`), never what to delete.
  //
  // A new document cannot tell a create that landed from one that failed: both come back to a blank new form, the
  // failed ones because several controllers redirect there on error, or the request never answered. So a copy that
  // was part of a Save is kept (it may be the only place the work exists) and tagged `was_submitted`, which keeps it
  // out of the banner: offering a document that may already exist invites a duplicate. It stays in the history,
  // where the clinician sees its time and chooses.
  async reconcileSubmitted() {
    const submitted = this.db.forms.where({ form_id: this.storageKey, submitted: 'true' });
    // Copies marked before Saves were told apart carry no `submit_id`; each is its own line, as before.
    const saveOf = (copy) => copy.submit_id || `copy:${copy.id}`;
    const newestOfEachSave = new Map();
    (await submitted.toArray()).forEach((copy) => {
      const save = saveOf(copy);
      const newest = newestOfEachSave.get(save);
      if (!newest || copy.id > newest.id) newestOfEachSave.set(save, copy);
    });
    const kept = new Set([...newestOfEachSave.values()].map((copy) => copy.id));
    // A kept copy the page already holds becomes part of this page's work, so the next Save here collapses it.
    // Otherwise a second refused Save would leave it behind as an older copy at the same version, and it would be
    // offered over the newer text.
    newestOfEachSave.forEach((copy) => {
      if (!draftDiffersFromForm(copy.children)) this.carryForward(copy);
    });

    await submitted.modify((draft, ref) => {
      // Marked by another tab after the read above, for a Save this page never saw: its next render settles it.
      if (!newestOfEachSave.has(saveOf(draft))) return;
      if (!kept.has(draft.id)) {
        delete ref.value;
        return;
      }
      draft.submitted = 'false';
      if (!this.recordVersion) draft.was_submitted = 'true';
    });
  }

  async markOwnCopiesSubmitted() {
    const { lineage } = this;
    const submitId = `${this.pageId}:${Date.now()}`;
    await this.histories.modify((copy) => {
      if (!(copy.id <= (lineage.get(copy.page_id) ?? -Infinity))) return;
      copy.submitted = 'true';
      copy.submit_id = submitId;
    });
  }

  // Makes a copy, and the copies of its page load before it, part of this page's work. A restore sets the cap to
  // the copy restored, so restoring an older copy after a newer one leaves the newer one out; a copy carried by
  // the cleanup only ever raises it. This page's own copies are always carried.
  carryForward(copy, { restored = false } = {}) {
    if (!copy.page_id || copy.page_id === this.pageId) return;
    const carried = this.lineage.get(copy.page_id) ?? -Infinity;
    this.lineage.set(copy.page_id, restored ? copy.id : Math.max(carried, copy.id));
  }

  // Tell a returning clinician that this browser holds work the record does not, instead of leaving them to
  // find the history button on their own.
  //
  // Only a copy that belongs to what is on screen is offered: one written against the version of the document the
  // server just rendered. Everything else stays in the history, where its timestamp is visible and the clinician
  // chooses. That covers a copy from before a later save (another tab, another device), which would put older text
  // over newer work, and a form with no version to compare: a blank new-record form cannot tell a create that
  // failed from one that landed, and offering that copy back would file the document twice.
  async offerUnsavedWork() {
    if (!this.unsavedWorkBanner || !this.ownsItsDrafts) return;

    const lastHistory = findUnsavedWork(await this.allCopiesNewestFirst(), (copy) => this.offerable(copy));
    if (!lastHistory) return;

    // A draft from the server may have been written on another device, so the banner does not say "this browser".
    this.unsavedWorkBanner.querySelectorAll('[data-source-browser]').forEach((element) => {
      element.hidden = !!lastHistory.fromServer;
    });
    this.unsavedWorkBanner.querySelectorAll('[data-source-server]').forEach((element) => {
      element.hidden = !lastHistory.fromServer;
    });
    this.unsavedWorkBanner.querySelectorAll('[data-saved-at]').forEach((savedAt) => {
      savedAt.textContent = new Date(lastHistory.time).toLocaleString(dateLocale());
    });
    // Say before the clinician chooses that part of this copy cannot come back, rather than offering it all.
    const partialRestore = this.unsavedWorkBanner.querySelector('[data-partial-restore]');
    if (partialRestore) partialRestore.hidden = !copyHoldsUnrestorableWork(lastHistory.children);

    this.onRestoreUnsavedWork = () => this.loadCopy(lastHistory);
    this.onDismissUnsavedWork = () => this.hideUnsavedWorkBanner();
    this.unsavedWorkBanner.querySelector('[data-restore]')?.addEventListener('click', this.onRestoreUnsavedWork);
    this.unsavedWorkBanner.querySelector('[data-dismiss-banner]')?.addEventListener('click', this.onDismissUnsavedWork);

    this.form.insertAdjacentElement('beforebegin', this.unsavedWorkBanner);
    this.unsavedWorkBanner.hidden = false;
  }

  // A copy written against the version on screen. On a new document there is no version, and a copy that was part
  // of a Save may be a document that already exists (see `reconcileSubmitted`).
  //
  // A new document also needs the copy's page tag. Copies written before tags existed were never marked on Save,
  // so there is no telling whether they became documents; they stay in the history.
  //
  // And only to the person who wrote it. One browser is often shared (a supervisor at a clinician's computer), the
  // banner says "Restore my work", and a restore saved under someone else's session would file the text as theirs.
  // Copies stay in the history either way, as they always have.
  offerable(copy) {
    if ((copy.user_id || null) !== currentUserId()) return false;
    if ((copy.record_version || null) !== this.recordVersion) return false;
    if (!this.recordVersion && !copy.page_id) return false;
    return copy.was_submitted !== 'true';
  }

  // A form tied to no record, no new-record id and no scope shares its bucket across patients and offers nothing.
  get ownsItsDrafts() {
    return !!this.recordVersion || this.form.id.startsWith('new_') || !!this.form.dataset.autoSaveScope;
  }

  hideUnsavedWorkBanner() {
    if (!this.unsavedWorkBanner) return;

    this.unsavedWorkBanner.hidden = true;
    this.unsavedWorkBanner.querySelector('[data-restore]')?.removeEventListener('click', this.onRestoreUnsavedWork);
    this.unsavedWorkBanner
      .querySelector('[data-dismiss-banner]')
      ?.removeEventListener('click', this.onDismissUnsavedWork);
  }

  async updateLastSavedAt() {
    const element = document.getElementById('auto-save-time-ago');
    if (!element) return;

    const relative = (time) => dayjs(time).locale(pageLocale()).fromNow();
    // While the server has the newest work, say so; when it could not be reached, the browser copy is what exists.
    if (this.server && !this.serverBehind && this.serverSavedAt) {
      const label = element.dataset.timeAgoServer || 'Draft saved: %{time}';
      element.textContent = label.replace('%{time}', relative(this.serverSavedAt));
      element.hidden = false;
      return;
    }

    const recentHistory = await this.histories.last();
    // A push may have landed while the browser copies were read.
    if (this.server && !this.serverBehind && this.serverSavedAt) return;
    if (recentHistory) {
      const label = element.dataset.timeAgo || 'Browser copy: %{time}';
      element.textContent = label.replace('%{time}', relative(recentHistory.time));
      element.hidden = false;
    } else {
      element.hidden = true;
    }
  }

  /**
   * @returns {Promise<any>}
   */
  get histories() {
    return this.db.forms.where({ form_id: this.storageKey, submitted: 'false' });
  }
}

// The newest copy that still differs from the page, from copies ordered newest first.
//
// A copy that matches the page is skipped, and what it says depends on where it came from. From a page load nobody
// touched, it is newer than the work but holds none of it, and must not hide it. From the same page load as older
// copies, it is the clinician's own latest state (typing undone), so those older copies are behind it and are not
// offered either. And a copy that was part of a Save (it carries a `submit_id`) means the page holds what that Save
// sent: a refused Save re-rendered with the clinician's work. Everything older was written before that Save, by
// this page or one the clinician moved past (an older restore, a goal card the form cannot re-render), and is left
// in the history rather than offered over the newer work.
function findUnsavedWork(copiesNewestFirst, offerable) {
  const settledPages = new Set();
  let settledBySave = false;
  return copiesNewestFirst.find((copy) => {
    if (settledBySave || !offerable(copy) || settledPages.has(copy.page_id)) return false;
    if (draftDiffersFromForm(copy.children)) return true;
    if (copy.submit_id) settledBySave = true;
    else if (copy.page_id) settledPages.add(copy.page_id);
    return false;
  });
}

// New-record forms (`form_for @new_record`) get a generic Rails-generated id
// like `new_care_plan` or `new_evaluation` regardless of which patient/org
// the form belongs to. Without scoping, every "new" form across patients
// shares one IndexedDB bucket. Edit forms already include the record UUID
// so they're scoped correctly — leave those alone to preserve drafts.
//
// Forms opt in to a semantic scope via `data-auto-save-scope` — e.g.
// "Evaluation;patient:abc;template:123". That stays stable across the
// session-timeout sign-in round-trip and discipline switches (which
// also swap templates and so still produce a different bucket — the
// template id is what identifies the form structure). Forms that don't
// opt in fall back to pathname + search (legacy behavior).
//
// A form with a fixed, hand-written id (`visit-note-main-form`) is in the same position whether the record is
// new or not, and opts in to a scope the same way.
function computeStorageKey(form) {
  const scope = form.dataset.autoSaveScope;
  if (form.id.startsWith('new_')) {
    if (scope) return `${form.id}::${scope}`;
    return `${form.id}::${window.location.pathname}${window.location.search}`;
  }
  return scope ? `${form.id}::${scope}` : form.id;
}

// The Save button is locked while the check runs, so a check that hangs (a busy database, a deploy restarting the
// app) must not hold the save hostage. A timeout is not an answer, so the submit goes ahead as it always has.
const SESSION_CHECK_TIMEOUT = 5000;

// Asks the server whether a submit would be accepted right now. An expired session answers 401. A live one
// answers with a fresh CSRF token, which replaces a token made stale by signing in again in another tab.
//
// Anything else is not an answer and the submit goes ahead as it always has: a network failure, a server error,
// or a 2xx that is not the JSON we asked for (maintenance mode redirects to an HTML page, and
// `maintenance_mode` exempts the sessions controller, not this one). Refusing to save on those would block
// clinicians for reasons that have nothing to do with their session.
async function checkSession() {
  try {
    const { data } = await axios.get('/session_status', { timeout: SESSION_CHECK_TIMEOUT });
    if (!data || typeof data !== 'object' || !data.user_id) return { expired: false };
    if (!sameUserAsPage(data.user_id)) return { expired: true, otherUser: data.user_name };

    refreshCsrfToken(data.csrf_token);
    return { expired: false };
  } catch (error) {
    // Signed in with a password but the two-factor code is not entered yet: a submit would land on that screen.
    if (error.response?.status === 403 && error.response.data?.reason === 'two_factor') {
      return { expired: true, twoFactor: true };
    }
    return { expired: error.response?.status === 401 };
  }
}

// The modal says two different things: signed out, or someone else signed into this browser. The second needs
// the other account signed out first, so a sign in link would only loop them back to "you are already signed in".
// The user this page was rendered for, from the layout's `current-user-id` meta. Null on layouts without it.
function currentUserId() {
  return metaValue('current-user-id') || null;
}

// How long one request to the server's draft may take. A draft that cannot be read or written in time is treated
// as unreachable: the browser copy is the backup.
const SERVER_DRAFT_TIMEOUT = 5000;

// Whether this user's organization keeps autosaved drafts on the server (FormDraftsController, :server_autosave).
function serverDraftsEnabled() {
  return metaValue('auto-save-server') === 'true';
}

// The user's drafts of one form on the server (FormDraftsController). Every call settles, never throws: a draft that
// cannot be reached leaves the browser copies to carry the work.
class ServerDrafts {
  constructor(formKey) {
    this.formKey = formKey;
  }

  // The drafts, newest first, or undefined when the server could not be asked. `recordVersion` lets the server drop
  // drafts whose Save has landed.
  async fetch(recordVersion) {
    try {
      const response = await axios.get('/form_draft', {
        params: { form_key: this.formKey, record_version: recordVersion || undefined },
        timeout: SERVER_DRAFT_TIMEOUT,
      });
      const drafts = response.data?.drafts;
      if (!Array.isArray(drafts)) return undefined;
      return drafts.filter((draft) => draft && Array.isArray(draft.snapshot) && draft.page_id);
    } catch {
      return undefined;
    }
  }

  // PUSH_STORED when the server now holds this copy (or a newer one of the same visit), PUSH_REFUSED when it will
  // never take it, PUSH_UNREACHABLE otherwise.
  async push(copy) {
    try {
      await axios.put(
        '/form_draft',
        {
          form_draft: {
            form_key: this.formKey,
            record_version: copy.record_version || null,
            page_id: copy.page_id,
            saved_at: copy.time,
            snapshot: copy.children,
          },
        },
        { timeout: SERVER_DRAFT_TIMEOUT }
      );
      return PUSH_STORED;
    } catch (error) {
      const status = error?.response?.status;
      const refused = status >= 400 && status < 500 && !RETRYABLE_STATUSES.includes(status);
      return refused ? PUSH_REFUSED : PUSH_UNREACHABLE;
    }
  }

  // Sent as the page leaves for a Save, so it uses a keepalive fetch: the browser finishes it after the page is gone,
  // and nothing waits for it. The CSRF token is read now, since the session check before a Save may have renewed it.
  markSubmitted(pageIds) {
    try {
      fetch('/form_draft/submit', {
        method: 'PATCH',
        keepalive: true,
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': metaValue('csrf-token') || '',
        },
        body: JSON.stringify({ form_key: this.formKey, page_ids: pageIds }),
      }).catch(() => {});
    } catch {
      // No fetch in this browser: the draft is left for the 30-day sweep.
    }
  }

  async remove() {
    try {
      await axios.delete('/form_draft', { params: { form_key: this.formKey }, timeout: SERVER_DRAFT_TIMEOUT });
    } catch {
      // Swept after 30 days if it cannot be removed now.
    }
  }
}

// The "saved" toast, saying where the copy went.
function showSavedToast(pushed) {
  const toast = document.getElementById('save-toast');
  if (toast) {
    toast.querySelectorAll('[data-saved-server]').forEach((element) => {
      element.hidden = !pushed;
    });
    toast.querySelectorAll('[data-saved-browser]').forEach((element) => {
      element.hidden = pushed;
    });
  }
  $('#save-toast').toast('show');
}

// The page's language, which the autosave menu carries: the patient portal is localized, staff pages are English.
function pageLocale() {
  return document.getElementById('auto-save-menu')?.dataset.locale || 'en';
}

// The locale for the dates and times shown in the banner and the history, written as before on English pages.
function dateLocale() {
  const locale = pageLocale();
  return locale === 'en' ? 'en-US' : locale;
}

// Shows the "a copy is in this browser" wording, or the "no copy could be kept" wording, inside `container`.
function showCopyState(container, copyStored) {
  if (!container) return;
  container.querySelectorAll('[data-copy-saved]').forEach((element) => {
    element.hidden = !copyStored;
  });
  container.querySelectorAll('[data-copy-not-saved]').forEach((element) => {
    element.hidden = copyStored;
  });
}

// Settles with `promise`, or rejects once `ms` have passed without it. The step itself is not cancelled; a late
// result is simply no longer waited for.
function withinTime(promise, ms) {
  let timer;
  const expired = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('timed out')), ms);
  });
  return Promise.race([Promise.resolve(promise), expired]).finally(() => clearTimeout(timer));
}

// The offline toast, saying whether this browser kept a copy of the work.
function showOfflineHold(copyStored) {
  showCopyState(document.getElementById('offline-form-saved'), copyStored);
  $('#offline-form-saved').toast('show');
}

// Returns whether there was a modal to show: the layout renders it on every authed page, but a page without it
// must not hold a Save with nothing on screen saying why.
function showSessionExpiredModal({ otherUser, twoFactor = false, copyStored = true }) {
  const modal = document.getElementById('auto-save-session-expired-modal');
  if (!modal) return false;
  showCopyState(modal, copyStored);
  const signedOut = modal.querySelector('[data-signed-out]');
  const other = modal.querySelector('[data-other-user]');
  const pendingTwoFactor = modal.querySelector('[data-two-factor]');
  if (signedOut) signedOut.hidden = !!otherUser || twoFactor;
  if (pendingTwoFactor) pendingTwoFactor.hidden = !twoFactor;
  if (other) {
    other.hidden = !otherUser;
    const name = other.querySelector('[data-other-user-name]');
    if (name) name.textContent = otherUser || '';
  }
  $('#auto-save-session-expired-modal').modal('show');
  return true;
}

// True when the draft holds work the rendered form does not.
//
// Fields only, never markup. A nested container is stored as a dump of its HTML alongside a snapshot of each
// field inside it, and that HTML cannot be compared: the container is given its id by `AddFields` on
// `turbo:load`, after this runs, and the dump is cleaned on the way in (`cleanHTML` strips add-field listener
// markers and selectize wrappers) while the live container is not. Comparing it called every custom form
// changed, which put the banner on documents nobody had touched.
//
// A field whose id the page no longer has is unsaved work only when its value is not somewhere else on the page.
// Two kinds of field lose their id between renders: goal cards added in the browser (a `Date.now()` key), whose
// text really is unsaved, and saved rows the page renumbers on every render (the diagnosis code control draws a
// random index into every name and id, `controls/smart/_patient_diagnosis_codes.html.erb:100`), whose values
// are all still there under new ids. `unmatchedPageValues` tells them apart.
//
// Hidden inputs are the one exception to "fields only". The field snapshot skips them on purpose (restoring one
// by id could overwrite a record id or a `_destroy` flag), but inside a nested block they carry real choices: the
// plan of care's area and skilled intervention picks live nowhere else. So for a nested block, the hidden values
// in its saved HTML are compared with the live block's. Values only: the names carry the same renumbered index.
// The live block is found by its `data-fields` name when it has no id yet (see above).
function draftDiffersFromForm(children) {
  const pool = unmatchedPageValues(children);
  const differs = (list) =>
    list.some((child) => {
      // A form region is kept as the fields it would submit (see `traverseDOM`), so it is compared
      // as those: a copy whose only unsaved work is a goal's session data or a plan's goals is
      // still work. A region the page no longer has differs when its copy holds anything.
      if (child.region) {
        const region = child.id ? document.getElementById(child.id) : null;
        if (!region) return (child.fields || []).some(([, value]) => hasContent(value));
        return !isEqual(child.fields || [], regionFields(region, region.dataset.autoSaveRegion));
      }
      if (child.htmlDump !== undefined) {
        const live = document.getElementById(child.id) || liveNestedBlock(child.id);
        if (live && hiddenValues(templateFor(child.htmlDump)) !== hiddenValues(live)) return true;
      }
      if (child.children) return differs(child.children);
      if (!child.id) return false;

      const field = document.querySelector(`#${CSS.escape(child.id)}:not([type="hidden"])`);
      if (!field) return hasContent(child.value) && !takeFromPool(pool, child.value);

      return fieldValue(field) !== child.value;
    });
  return differs(children);
}

// True when the copy holds work that a restore cannot put back: content in a field the page no longer has, outside
// a nested block, and not merely renumbered (see `draftDiffersFromForm`). A restore matches saved values to live
// fields by id, so a goal card added in the browser has nowhere to go. A nested block (areas of intervention) is
// put back whole from its saved HTML, ids included, so work inside one does come back.
function copyHoldsUnrestorableWork(children) {
  const pool = unmatchedPageValues(children);
  const unrestorable = (list) =>
    list.some((child) => {
      // A region's copy goes back whole through the region (`restoreRegion`); only a region the
      // page no longer has leaves its content with nowhere to go.
      if (child.region) {
        return (
          !(child.id && document.getElementById(child.id)) &&
          (child.fields || []).some(([, value]) => hasContent(value))
        );
      }
      if (child.htmlDump !== undefined) return false;
      if (child.children) return unrestorable(child.children);
      if (!child.id) return false;
      if (document.querySelector(`#${CSS.escape(child.id)}:not([type="hidden"])`)) return false;

      return hasContent(child.value) && !takeFromPool(pool, child.value);
    });
  return unrestorable(children);
}

// How many times each value appears in the page's own fields that the copy has no field for. A copy value found
// here was renumbered, not lost. Counted, so two copy fields cannot both claim one page value.
function unmatchedPageValues(children) {
  const copyIds = new Set();
  const collect = (list) =>
    list.forEach((child) => {
      if (child.id) copyIds.add(child.id);
      if (child.children) collect(child.children);
    });
  collect(children);

  const pool = new Map();
  document.querySelectorAll('input, textarea, select, trix-editor').forEach((field) => {
    if (!isHTMLFieldLike(field) || !field.id || copyIds.has(field.id)) return;
    const value = fieldValue(field);
    if (!hasContent(value)) return;
    pool.set(String(value), (pool.get(String(value)) || 0) + 1);
  });
  return pool;
}

function takeFromPool(pool, value) {
  const count = pool.get(String(value)) || 0;
  if (count === 0) return false;
  pool.set(String(value), count - 1);
  return true;
}

function fieldValue(field) {
  return isCheckable(field) ? field.checked : field.value;
}

const NESTED_CONTAINER_SUFFIX = '_nested_field_container';

// AddFields names a nested block `${target}_nested_field_container` after its `data-fields="${target}"`.
function liveNestedBlock(id) {
  if (!id?.endsWith(NESTED_CONTAINER_SUFFIX)) return null;
  const target = id.slice(0, -NESTED_CONTAINER_SUFFIX.length);
  return document.querySelector(`[data-fields="${CSS.escape(target)}"]`);
}

function templateFor(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content;
}

function hiddenValues(root) {
  return Array.from(root.querySelectorAll('input[type="hidden"]'))
    .map((input) => input.value)
    .sort()
    .join('\n');
}

function hasContent(value) {
  return value !== undefined && value !== null && value !== '' && value !== false;
}

/**
 * @description Walk the DOM recursively and create an object with the following data structure for the given DOM:
 * @example
 * <div>
 *   <input id="1" type="text" name="care_plan_title" />
 *   <div id="some_id" data-fields="some_nested_field">
 *     <textarea id="2" name="care_plan[long_term_goals][235245][goal]"></textarea>
 *     <div data-fields="short_term_goal">
 *       <textarea id="3" name="care_plan[long_term_goals][235245][short_term_goals][1235989][goal]"></textarea>
 *     </div>
 *   </div>
 * </div>
 *
 * [
 *   {
 *     id: "1",
 *     nodeName: "INPUT",
 *     name: "care_plan_title",
 *     value: "",
 *     isSelectize: false
 *   },
 *   {
 *     htmlDump: "<textarea name="care_plan[long_term_goals][235245][goal]">...</textarea><div>...</div>",
 *     id: "some_id",
 *     children: [
 *       {
 *         id: "2",
 *         nodeName: "TEXTAREA",
 *         name: "care_plan[long_term_goals][235245][goal]",
 *         value: "",
 *         isSelectize: false,
 *       },
 *       {
 *         // more objects
 *       }
 *     ]
 *   }
 * ]
 *
 * A form region (see helpers/form-region.js) is kept whole, as the fields it would submit, and restores through its
 * own controller:
 *   {
 *     id: "goals-v3-capture-region-control_1",
 *     nodeName: "DIV",
 *     region: true,
 *     fields: [["visit_note[datum][control_1][goals][<goal id>][landed]", "1"], ...]
 *   }
 * @param {Node} node
 * @param {Node[]} children
 * @param {boolean} makeDump
 */
function traverseDOM(node, children = [], makeDump = true) {
  // Note: This is not the same as `const childrenDup = [...children]` as that would be cloning the elements. When
  // traversing, we need the exact reference of the Node to successfully transfer any kind of `addEventListener` that
  // has been already attached
  const childrenDup = children;

  for (let i = 0; i < node.childNodes.length; i += 1) {
    const currentNode = node.childNodes[i];

    if (isFormRegion(currentNode)) {
      childrenDup.push({
        id: currentNode.id,
        nodeName: currentNode.nodeName,
        region: true,
        fields: regionFields(currentNode, currentNode.dataset.autoSaveRegion),
      });
    } else if (isTopLevelNestedContainer(currentNode) && makeDump) {
      childrenDup.push({
        children: traverseDOM(currentNode, [], false),
        htmlDump: cleanHTML(currentNode),
        id: currentNode.id,
        nodeName: currentNode.nodeName,
      });
    } else if (isHTMLFieldLike(currentNode) && !!currentNode.id && !currentNode.id.includes('selectized')) {
      childrenDup.push({
        id: currentNode.id,
        isSelectize: isSelectizeInstance(currentNode),
        nodeName: currentNode.nodeName,
        name: currentNode.name,
        type: getElementType(currentNode),
        value: isCheckable(currentNode) ? currentNode.checked : currentNode.value,
      });
    } else {
      traverseDOM(currentNode, childrenDup);
    }
  }

  return childrenDup;
}

function isHTMLFieldLike(node) {
  return (
    (node instanceof HTMLInputElement && node.type !== 'hidden' && node.type !== 'submit') ||
    node instanceof HTMLTextAreaElement ||
    node instanceof HTMLSelectElement ||
    node.nodeName === 'TRIX-EDITOR'
  );
}

// A form region's copy goes back to the region, whose controller rebuilds its rows and puts the values back.
function restoreRegion({ id, fields }) {
  const region = id ? document.getElementById(id) : null;
  if (!region) return;

  region.dispatchEvent(new CustomEvent('auto-save:restore', { detail: { fields: fields || [] } }));
}

function findAndUpdateElement(object) {
  if (object.region) {
    restoreRegion(object);
    return;
  }

  const { id, value, isSelectize } = object;
  if (!id) return;
  // Looks like selectize adds a hidden input field with the same `id`.
  const htmlField = document.querySelector(`#${CSS.escape(id)}:not([type="hidden"])`);
  if (!htmlField) return;

  if (htmlField.classList.contains('hidden')) {
    const container = document.querySelector(`div[data-id="${CSS.escape(id)}"]`);
    if (!container) return;

    const autoSaveLoad = new CustomEvent('auto-save-load', {
      detail: {
        data: value,
      },
    });
    container.dispatchEvent(autoSaveLoad);
  }

  if (isSelectize) {
    if (isSelectizeInstance(htmlField)) {
      htmlField.selectize.addItem(value);
    } else {
      // In an ideal world, these attributes should've been initialized within the select field as HTML attributes
      $(htmlField).selectize({
        create: false,
        sortField: 'id',
        placeholder: htmlField.getAttribute('placeholder'),
        onDropdownClose(dropdown) {
          $(dropdown).find('.selected').not('.active').removeClass('selected');
        },
      });
    }

    return;
  }

  if (isCheckable(htmlField)) {
    htmlField.checked = value;
    return;
  }

  htmlField.value = value;
}

// An Impulse autocomplete keeps its pick in a hidden field (the value, and the text in `data-text`), which comes
// back with a nested block's saved HTML. The text it shows is typed into a text input, which does not, and the
// diagnosis code rows all give that input the same id, so restoring it by id put every row's text into the first
// row. Show each single-select autocomplete the text of the pick it will submit, and nothing when it has no pick
// (a fresh row renders without `data-text`), so no row shows a code it does not submit.
function showRestoredPicks(container) {
  container.querySelectorAll('awc-autocomplete').forEach((autocomplete) => {
    const pick = autocomplete.querySelector('input[data-behavior="hidden-field"]:not([multiple])');
    const input = autocomplete.querySelector('.awc-autocomplete-input');
    if (pick && input) input.value = pick.getAttribute('data-text') ?? '';
  });
}

function isCheckable(node) {
  return ['checkbox', 'radio'].includes(node.type);
}

function isSelectizeInstance(node) {
  return typeof node.selectize === 'object';
}

function isFormRegion(node) {
  return node instanceof HTMLElement && node.hasAttribute('data-auto-save-region') && !!node.id;
}

function isTopLevelNestedContainer(node) {
  return node instanceof HTMLElement && node.hasAttribute('data-nested') && !node.parentNode.closest('[data-nested]');
}

function cleanHTML(node) {
  const parser = new DOMParser();
  const parsedHTML = parser.parseFromString(node.innerHTML, 'text/html');

  // Remove `data-add-links-listener` attribute before making a dump so that we can initialize later.
  // See: app/javascript/helpers/nested-fields/addFields.js#L25
  const attribute = 'data-add-links-listener';
  Array.from(parsedHTML.querySelectorAll(`[${attribute}]`)).forEach((s) => s.removeAttribute(attribute));

  // Add selectize options back to select fields
  const selects = Array.from(node.querySelectorAll('select')).filter((s) => isSelectizeInstance(s));
  selects.forEach((select) => {
    const inputId = select.selectize.$input[0].id;
    const selectField = parsedHTML.getElementById(inputId);
    if (!selectField) return;

    copySelectizeAttributes(select, selectField);
  });

  // Remove instances of `selectize` before making a dump. If we pair up `selectize` and `stimulus`, this won't be necessary
  Array.from(parsedHTML.querySelectorAll('.selectize-control')).forEach((s) => s.remove());
  return parsedHTML.body.innerHTML;
}

function copySelectizeAttributes(fromSelect, toSelect) {
  toSelect.required = fromSelect.selectize.isRequired;
  toSelect.setAttribute('placeholder', fromSelect.selectize.$control_input[0].placeholder);

  const optionValues = Array.from(toSelect.options).map(({ value }) => value);
  Object.entries(fromSelect.selectize.options).forEach(([key, object]) => {
    if (optionValues.includes(key)) return;

    const option = document.createElement('option');
    option.value = key;
    option.innerText = object.text;
    option.disabled = object.disabled;
    option.selected = fromSelect.selectize.items.includes(object.value);
    toSelect.append(option);
  });
}

function getElementType(node) {
  if (node.type) {
    return node.type;
  }

  if (typeof customElements.get(node.tagName.toLowerCase()) === 'function') {
    return 'custom-element';
  }

  return 'unknown';
}
